// src/hooks/useFigureMatchGameLogic.js
import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { shuffleArray } from '../utils/helpers';
import useFeedbackPrompt from './useFeedbackPrompt';

// ✅ UPDATED: Added pairsToMatch and isJourneyMode parameters
const useFigureMatchGameLogic = ({ navigate, preloadedData, pairsToMatch = null, isJourneyMode = false }) => {
    const { user } = useAuth();
    const backendApiUrl = process.env.REACT_APP_BACKEND_API_URL;
    const ACTIVITY_TYPE = "islamic_figures_match";

    // --- Core Game State Management ---
    const [cards, setCards] = useState([]);
    const [flippedCards, setFlippedCards] = useState([]);
    const [matchedPairs, setMatchedPairs] = useState([]);
    const [moves, setMoves] = useState(0);
    const [gameOver, setGameOver] = useState(false);
    const [lives, setLives] = useState(5);
    const [streak, setStreak] = useState(0);
    const [negativeStreak, setNegativeStreak] = useState(0);
    const [shakingCards, setShakingCards] = useState([]);

    // --- UI State Management ---
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [scoreSubmitted, setScoreSubmitted] = useState(false);
    const [currentFigureBiography, setCurrentFigureBiography] = useState(null);
    const [showBiographyModal, setShowBiographyModal] = useState(false);
    const [showReviewModal, setShowReviewModal] = useState(false);
    const [figuresInCurrentGame, setFiguresInCurrentGame] = useState([]);
    const [showStreakPopup, setShowStreakPopup] = useState(false);
    const [streakMessage, setStreakMessage] = useState('');
    const [showGameOverPopup, setShowGameOverPopup] = useState(false);
    const [showEncouragementPopup, setShowEncouragementPopup] = useState(false);
    const [encouragementMessage, setEncouragementMessage] = useState('');
    // Removed local feedback modal state - use centralized hook below

    // --- Refs for Timers ---
    const timerRef = useRef(null);
    const startTimeRef = useRef(null);
    const encouragementPopupTimerRef = useRef(null);
    const streakPopupTimerRef = useRef(null);

    // Centralized feedback prompt hook
    const feedbackPrompt = useFeedbackPrompt({
        activityKey: ACTIVITY_TYPE,
        isJourney: isJourneyMode,
        navigate,
    });

    // Score calculation logic
    const calculateScore = useCallback((matchedPairsCount, durationSeconds) => {
        const baseScore = matchedPairsCount * 5;
        const timePenalty = Math.floor(durationSeconds / 10) * 5;
        let calculatedScore = baseScore - timePenalty;
        return Math.max(0, calculatedScore);
    }, []);

    const submitScoreToLeaderboard = useCallback(async (finalScore) => {
        // Don't submit scores in journey mode or for guest users
        if (isJourneyMode || (user && user.uid === "guest-user")) {
            console.log("Journey mode or guest user - score not submitted to leaderboard.");
            return;
        }

        if (!user || scoreSubmitted) return;
        if (!user.uid || !user.username) {
            console.error("User ID or username missing for score submission.");
            return;
        }

        try {
            await axios.post(`${backendApiUrl}/scores`, {
                userId: user.uid,
                username: user.username,
                score: finalScore,
                activityType: ACTIVITY_TYPE,
                avatar: user.avatar || user.photoURL || '/default-avatar.png'
            });
            setScoreSubmitted(true);
        } catch (error) {
            console.error(`Failed to submit Figure Match score:`, error.response ? error.response.data : error.message);
        }
    }, [user, scoreSubmitted, ACTIVITY_TYPE, backendApiUrl, isJourneyMode]);

    // Handle game completion
    const handleGameCompletion = useCallback(() => {
        setGameOver(true);
        const endTime = Date.now();
        const durationSeconds = Math.floor((endTime - startTimeRef.current) / 1000);

        const matchedPairsCount = new Set(cards.filter(card => card.isMatched).map(card => card.pairId)).size;

        const score = calculateScore(matchedPairsCount, durationSeconds);
        submitScoreToLeaderboard(score);

        const uniqueFigures = Array.from(new Set(cards.filter(card => card.type === 'name').map(card => card.pairId)))
            .map(pairId => cards.find(card => card.pairId === pairId && card.type === 'name'));
        setFiguresInCurrentGame(uniqueFigures);

        // ✅ Only show feedback modal in standalone mode
        if (!isJourneyMode) {
            // Delegate feedback prompting to shared hook which handles localStorage bookkeeping
            feedbackPrompt.showIfAppropriate();
        }
    }, [cards, calculateScore, submitScoreToLeaderboard, isJourneyMode, feedbackPrompt]);

    // ✅ UPDATED: Fetch cards with pair filtering for journey mode
    const fetchCards = useCallback(async () => {
        if (!user) {
            setLoading(false);
            setError("Please log in to play this game.");
            return;
        }

        setLoading(true);
        setError(null);

        try {
            let allFigures;

            // Use preloaded data if available
            if (preloadedData && preloadedData.figurematch) {
                allFigures = preloadedData.figurematch;
            } else {
                const response = await axios.get(`${backendApiUrl}/figurematchgame/questions`);
                allFigures = response.data;
            }

            if (!Array.isArray(allFigures) || allFigures.length === 0) {
                setError("Invalid or empty game data received from backend.");
                setLoading(false);
                return;
            }

            // ✅ Determine how many pairs to use
            const availablePairs = new Set(allFigures.map(card => card.pairId));
            let numberOfPairs;

            if (pairsToMatch !== null && isJourneyMode) {
                // Journey mode: limit to specified pairs
                numberOfPairs = Math.min(pairsToMatch, availablePairs.size);
                console.log(`🕌 Journey Mode: Loading ${numberOfPairs} pairs`);
            } else {
                // Standalone mode: use all pairs
                numberOfPairs = availablePairs.size;
                console.log(`🎮 Standalone Mode: Loading all ${numberOfPairs} pairs`);
            }

            // ✅ Select and shuffle pairs
            const shuffledPairIds = shuffleArray(Array.from(availablePairs)).slice(0, numberOfPairs);
            const selectedFigures = allFigures.filter(card => shuffledPairIds.includes(card.pairId));

            const nameCards = [];
            const attributeCards = [];

            shuffledPairIds.forEach(pairId => {
                const nameCard = selectedFigures.find(c => c.pairId === pairId && c.type === 'name');
                const attributeCard = selectedFigures.find(c => c.pairId === pairId && c.type === 'attribute');

                if (nameCard && attributeCard) {
                    nameCards.push({
                        ...nameCard,
                        instanceId: `${nameCard.pairId}-name-${Math.random()}`,
                        isFlipped: true,
                        isMatched: false
                    });
                    attributeCards.push({
                        ...attributeCard,
                        instanceId: `${attributeCard.pairId}-attribute-${Math.random()}`,
                        isFlipped: false,
                        isMatched: false
                    });
                }
            });

            if (nameCards.length === 0 || nameCards.length !== attributeCards.length) {
                setError("Insufficient or mismatched pairs found in the fetched game data.");
                setLoading(false);
                return;
            }

            const shuffledNameCards = shuffleArray(nameCards);
            const shuffledAttributeCards = shuffleArray(attributeCards);

            setCards([...shuffledNameCards, ...shuffledAttributeCards]);
            setLoading(false);
            setMoves(0);
            setFlippedCards([]);
            setMatchedPairs([]);
            setGameOver(false);
            setScoreSubmitted(false);
            setShakingCards([]);
            setLives(5);
            setStreak(0);
            setNegativeStreak(0);
            setShowGameOverPopup(false);
            setShowStreakPopup(false);
            setShowEncouragementPopup(false);
            setCurrentFigureBiography(null);
            setShowBiographyModal(false);
            setShowReviewModal(false);
            setFiguresInCurrentGame([]);
            startTimeRef.current = Date.now();
        } catch (err) {
            setError(`Failed to load game cards: ${err.message}.`);
            setLoading(false);
        }
    }, [user, backendApiUrl, preloadedData, pairsToMatch, isJourneyMode]);

    useEffect(() => {
        if (user) {
            fetchCards();
        }
    }, [user, fetchCards]);

    // Handle card click logic
    const handleCardClick = useCallback((clickedCard) => {
        if (gameOver || shakingCards.length > 0 || matchedPairs.includes(clickedCard.pairId) || flippedCards.some(card => card.instanceId === clickedCard.instanceId)) {
            return;
        }

        if (flippedCards.length === 1 && flippedCards[0].type === clickedCard.type) {
            return;
        }

        const newFlippedCards = [...flippedCards, clickedCard];
        setFlippedCards(newFlippedCards);

        setCards(prevCards => prevCards.map(card =>
            card.instanceId === clickedCard.instanceId ? { ...card, isFlipped: true } : card
        ));

        if (newFlippedCards.length === 2) {
            setMoves(prev => prev + 1);

            const card1 = newFlippedCards[0];
            const card2 = newFlippedCards[1];

            const nameCard = card1.type === 'name' ? card1 : card2;
            const attributeCard = card1.type === 'attribute' ? card1 : card2;

            if (nameCard && attributeCard && nameCard.pairId === attributeCard.pairId) {
                // Correct match
                setMatchedPairs(prev => [...prev, nameCard.pairId]);
                setCards(prevCards => prevCards.map(card =>
                    (card.instanceId === nameCard.instanceId || card.instanceId === attributeCard.instanceId)
                        ? { ...card, isMatched: true, isFlipped: true }
                        : card
                ));
                setFlippedCards([]);
                setCurrentFigureBiography(nameCard);
                setShowBiographyModal(true);
                setNegativeStreak(0);
                setStreak(prev => prev + 1);
            } else {
                // Incorrect match
                setShakingCards([card1.instanceId, card2.instanceId]);
                setStreak(0);
                setNegativeStreak(prev => prev + 1);

                // ✅ Only deduct lives in standalone mode
                if (!isJourneyMode) {
                    setLives(prevLives => prevLives - 1);
                }
            }
        }
    }, [gameOver, shakingCards, matchedPairs, flippedCards, isJourneyMode]);

    // Check for game over condition
    useEffect(() => {
        const totalUniquePairs = new Set(cards.map(c => c.pairId)).size;
        if (matchedPairs.length > 0 && matchedPairs.length === totalUniquePairs) {
            setGameOver(true);
            handleGameCompletion();
        } else if (!isJourneyMode && lives <= 0 && !gameOver) {
            // ✅ Only trigger game over on 0 lives in standalone mode
            setGameOver(true);
            setShowGameOverPopup(true);
            handleGameCompletion();
        }
    }, [matchedPairs, cards, lives, gameOver, handleGameCompletion, isJourneyMode]);

    // Effect for handling no-match and shaking cards
    useEffect(() => {
        if (flippedCards.length === 2) {
            const card1 = flippedCards[0];
            const card2 = flippedCards[1];
            if (card1.pairId !== card2.pairId) {
                timerRef.current = setTimeout(() => {
                    setCards(prevCards => prevCards.map(card => {
                        if (card.instanceId === card1.instanceId || card.instanceId === card2.instanceId) {
                            return card.type === 'attribute' ? { ...card, isFlipped: false } : card;
                        }
                        return card;
                    }));
                    setFlippedCards([]);
                    setShakingCards([]);
                }, 1200);
            }
        }
        return () => clearTimeout(timerRef.current);
    }, [flippedCards]);

    // ✅ Streak popup (only in standalone mode)
    useEffect(() => {
        if (!isJourneyMode && streak > 0 && streak % 3 === 0) {
            if (streakPopupTimerRef.current) {
                clearTimeout(streakPopupTimerRef.current);
            }
            setShowStreakPopup(true);
            setStreakMessage(`${streak} in a row, Masha'Allah!`);
            streakPopupTimerRef.current = setTimeout(() => {
                setShowStreakPopup(false);
                setStreakMessage('');
            }, 2500);
        }
    }, [streak, isJourneyMode]);

    // ✅ Encouragement popup (only in standalone mode)
    useEffect(() => {
        if (!isJourneyMode && negativeStreak > 0 && negativeStreak % 2 === 0) {
            if (encouragementPopupTimerRef.current) {
                clearTimeout(encouragementPopupTimerRef.current);
            }
            setEncouragementMessage("Keep going!");
            setShowEncouragementPopup(true);
            encouragementPopupTimerRef.current = setTimeout(() => {
                setShowEncouragementPopup(false);
                setEncouragementMessage('');
            }, 2500);
        }
    }, [negativeStreak, isJourneyMode]);

    const resetGame = useCallback(() => {
        if (timerRef.current) clearTimeout(timerRef.current);
        if (encouragementPopupTimerRef.current) clearTimeout(encouragementPopupTimerRef.current);
        if (streakPopupTimerRef.current) clearTimeout(streakPopupTimerRef.current);
        setCards([]);
        setFlippedCards([]);
        setMatchedPairs([]);
        setMoves(0);
        setGameOver(false);
        setLoading(true);
        setScoreSubmitted(false);
        setShakingCards([]);
        setLives(5);
        setStreak(0);
        setNegativeStreak(0);
        setShowGameOverPopup(false);
        setShowStreakPopup(false);
        setShowEncouragementPopup(false);
        setCurrentFigureBiography(null);
        setShowBiographyModal(false);
        setShowReviewModal(false);
        setFiguresInCurrentGame([]);
        // Also hide the shared feedback modal when resetting
        if (feedbackPrompt && typeof feedbackPrompt.setShowFeedbackModal === 'function') {
            feedbackPrompt.setShowFeedbackModal(false);
        }
        fetchCards();
    }, [fetchCards, feedbackPrompt]);

    const closeBiographyModal = useCallback(() => setShowBiographyModal(false), []);
    const closeReviewModal = useCallback((triggerReview = false) => {
        setShowReviewModal(triggerReview);
    }, []);

    const handleCloseFeedbackModal = feedbackPrompt.handleCloseFeedbackModal;
    const handleDismissFeedbackPermanently = feedbackPrompt.handleDismissFeedbackPermanently;

    return {
        loading,
        error,
        cards,
        matchedPairs,
        lives,
        moves,
        gameOver,
        shakingCards,
        currentFigureBiography,
        figuresInCurrentGame,
        showBiographyModal,
        showReviewModal,
        showStreakPopup,
        streakMessage,
        showGameOverPopup,
        showEncouragementPopup,
        encouragementMessage,
        showFeedbackModal: feedbackPrompt.showFeedbackModal,
        streak,
        startTimeRef,
        handleCardClick,
        resetGame,
        closeBiographyModal,
        closeReviewModal,
        handleCloseFeedbackModal,
        handleDismissFeedbackPermanently,
        calculateScore,
    };
};

export default useFigureMatchGameLogic;
